import React, { useState } from 'react';
import { Trash2, AlertTriangle, Lock } from 'lucide-react';
import { Task } from '../../types';

interface DeleteTaskModalProps {
  task: Task;
  onConfirm: (password: string) => Promise<void>;
  onCancel: () => void;
}

const DeleteTaskModal: React.FC<DeleteTaskModalProps> = ({ task, onConfirm, onCancel }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password) {
      setError('Veuillez entrer le mot de passe');
      return;
    }
    
    setError(null);
    setIsSubmitting(true);
    
    try {
      await onConfirm(password);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue lors de la suppression');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-2 text-red-600 mb-4">
          <Trash2 className="h-6 w-6" />
          <h2 className="text-xl font-bold">Supprimer la tâche</h2>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-red-700 font-medium">Attention : Action irréversible</p>
              <p className="text-sm text-red-600 mt-1">
                Cette action va définitivement supprimer la tâche et toutes les données associées.
                Cette opération ne peut pas être annulée.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
              Mot de passe de suppression
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Entrez le mot de passe de suppression"
                autoComplete="off"
              />
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Annuler
            </button>

            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !password}
            >
              {isSubmitting ? 'Suppression...' : 'Supprimer définitivement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeleteTaskModal;